import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Spinner} from 'react-bootstrap';
import {BookmarkRemove} from '@mui/icons-material';
import {CircularProgress} from '@mui/material';
import {toast} from 'react-toastify';
import {AddRestockedToCartButton} from './AddRestockedToCartButton';
import {isRestocked} from '../application/useRestockedTrackedProducts';
import {untrackProduct} from '../infrastructure/productApi';
import {isSuccess, isPending, isFailure} from '../../common/domain/RemoteData.model';
import {PriceWithFallback} from '../../common/ui/PriceWithFallback';
import {useComercialContext} from '../../comercial/ComercialContext';

const UntrackButton = ({product, onUntracked}) => {
    const [loading, setLoading] = useState(false);
    const {actingAsCliente} = useComercialContext();

    const handleClick = async () => {
        setLoading(true);
        try {
            const result = await untrackProduct(product.code, actingAsCliente?.numCliente);
            if (isSuccess(result)) {
                toast.success('Producto quitado de tus productos pendientes!');
                onUntracked(product.code);
            } else {
                toast.error('Error al quitar producto de tus productos pendientes');
            }
        } catch (error) {
            console.error('Failed to untrack product:', error);
            toast.error('Error al quitar producto de tus productos pendientes');
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button"
            className="btn btn-outline-warning"
            title="Quitar de pendientes"
            onClick={handleClick}
            disabled={loading}>
            {loading ? <CircularProgress size={20}/> : <BookmarkRemove/>}
        </button>
    );
};

/**
 * Panel desplegable de productos pendientes (equivalente al CartPreview
 * del carrito): lista los pendientes, y los que ya tienen stock
 * suficiente se pueden añadir al carrito directamente.
 *
 * @param {{trackedProducts: Object, setShowPanel: (show: boolean) => void}} props
 * trackedProducts es el resultado de useRestockedTrackedProducts.
 */
export const TrackedPreview = ({trackedProducts, setShowPanel}) => {
    const {trackedData, allTracked, refresh, removeLocally} = trackedProducts;
    const navigate = useNavigate();

    const handleGoToProduct = (code) => {
        setShowPanel(false);
        navigate(`/articulos/${encodeURIComponent(code)}`);
    };

    const handleGoToAll = () => {
        setShowPanel(false);
        navigate('/cuenta/pendientes');
    };

    return (
        <div className="d-flex flex-column gap-3 p-2">
            <h2 className="h5 mb-0">Productos pendientes</h2>
            {isPending(trackedData) && (
                <div className="text-center my-3">
                    <Spinner/>
                </div>
            )}
            {isFailure(trackedData) && (
                <div className="alert alert-danger py-2 mb-0">
                    No se pudieron cargar tus productos pendientes.
                </div>
            )}
            {isSuccess(trackedData) && allTracked.length === 0 && (
                <span className="text-muted">No tienes productos pendientes.</span>
            )}
            {isSuccess(trackedData) && allTracked.length > 0 && (
                <ul className="list-group">
                    {allTracked.map((product) => (
                        <li key={product.code} className="list-group-item">
                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                                <img src={product.imageUrl}
                                    alt={product.name}
                                    style={{width: '48px', height: '48px', objectFit: 'contain', cursor: 'pointer'}}
                                    onClick={() => handleGoToProduct(product.code)}
                                />
                                <div className="flex-fill">
                                    <span role="button" onClick={() => handleGoToProduct(product.code)}>
                                        {product.name}
                                    </span>
                                    <br/>
                                    <small className="text-muted">
                                        REF: {product.code} · Querías {product.trackedQuantity ?? 1} ud.
                                    </small>
                                    <div>
                                        <PriceWithFallback data={{
                                            sellingPrice: product.price?.bestPrice,
                                            basePrice: product.price?.value
                                        }}/>
                                    </div>
                                </div>
                                <div className="d-flex flex-row gap-2 align-items-center">
                                    {isRestocked(product) ? (
                                        <div style={{minWidth: '190px'}}>
                                            <AddRestockedToCartButton
                                                product={product}
                                                onAdded={removeLocally}
                                                onFailed={refresh}
                                            />
                                        </div>
                                    ) : (
                                        <span className="badge bg-secondary">Sin stock</span>
                                    )}
                                    <UntrackButton product={product} onUntracked={removeLocally}/>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-link p-0" onClick={handleGoToAll}>
                    Ver todos los pendientes
                </button>
            </div>
        </div>
    );
};
